import { useEffect, useRef } from 'react';
import type { EChartsOption } from 'echarts';
import { echarts, SN_ECHARTS_THEME } from './echartsSetup';

type EChartsInstance = ReturnType<typeof echarts.init>;

/**
 * Mounts an ECharts instance (SVG renderer + SN theme) into a div ref and
 * keeps it sized to its container. Re-applies `option` whenever it changes.
 * Returns the ref to attach to a sized container element.
 */
export function useECharts(option: EChartsOption) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<EChartsInstance | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const chart = echarts.init(el, SN_ECHARTS_THEME, { renderer: 'svg' });
    chartRef.current = chart;
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(option, true);
  }, [option]);

  return containerRef;
}
